import {
  COMMENT_REGION_WIDTH,
  MARGIN_BETWEEN_COMMENT_GROUPS,
  PEN_POINTER_TYPE,
  TOUCH_POINTER_TYPE
} from './constants'
import curry from 'lodash/curry'
import flow from 'lodash/flow'
import lodashSortBy from 'lodash/sortBy'
import round from 'lodash/round'

export const pipe = (arg1, ...args) => flow([...args])(arg1)

export const isNot = a => b => b !== a

export const sortBy = curry((fn, arr) => lodashSortBy(arr, fn))

export function compareRectY(a, b) {
  return a.rect[0][1] > b.rect[0][1] ? -1 : 1
}

export function compareRectYInverse(a, b) {
  return compareRectY(a, b) === 1 ? -1 : 1
}

export const hasReplyIn = allReplies => annotation =>
  allReplies.some(reply => reply.inreplyto === annotation.id)

export const isOriginalTopAbove = topOfActiveComment => ({ pageTop, div }) =>
  pageTop + parseFloat(div.dataset.originalTop) < topOfActiveComment

export const isBelowOriginalTop = topOfActiveComment => ({ pageTop, div }) =>
  pageTop + parseFloat(div.dataset.originalTop) > topOfActiveComment

export const splitRootAnnotationsAndReplies = (
  [replies, rootAnnotations],
  annotation
) =>
  annotation.inreplyto
    ? [replies.concat(annotation), rootAnnotations]
    : [replies, rootAnnotations.concat(annotation)]

export function getMostVisiblePage(visiblePages) {
  let mostVisiblePageNumber = '0'
  let highestIntersectionRatio = 0
  for (const pageNumber in visiblePages) {
    if (visiblePages.hasOwnProperty(pageNumber)) {
      if (visiblePages[pageNumber] > highestIntersectionRatio) {
        mostVisiblePageNumber = pageNumber
        highestIntersectionRatio = visiblePages[pageNumber]
      }
    }
  }
  return parseInt(mostVisiblePageNumber, 10)
}

export function getPageWidthAfterScaling(displayCommentRegion, viewportWidth) {
  return (
    Math.trunc(viewportWidth) +
    (displayCommentRegion ? COMMENT_REGION_WIDTH : 0)
  )
}

export function getStringValues(object) {
  let stringValues = []
  for (let colorName in object) {
    if (object.hasOwnProperty(colorName)) {
      const colorValue = object[colorName]
      stringValues.push(colorValue)
    }
  }
  return stringValues
}

export const getGroupDivsWithPageData = pagesDiv => {
  // Get all comment group divs accompanied by pageTop
  // MS Edge returns 0 items if we use querySelectorAll('.Page') ¯\_(ツ)_/¯
  const pageDivs = [...pagesDiv.querySelectorAll('.PageComponent')]

  const allCommentGroupDivsWithPageData = pageDivs.reduce((state, pageDiv) => {
    const commentGroupDivs = [...pageDiv.querySelectorAll('.CommentGroup')]

    if (commentGroupDivs.length === 0) {
      return state
    }

    // pageDiv.offsetTop is expensive, so we minimize calling it
    const pageTop = pageDiv.offsetTop

    return state.concat(
      commentGroupDivs.map(div => ({
        pageTop,
        div
      }))
    )
  }, [])

  const activeCommentGroupDiv = pagesDiv.querySelector(
    '.Annotation--selected .CommentGroup'
  )

  const inactiveCommentGroupDivsWithPageData = allCommentGroupDivsWithPageData.filter(
    ({ div }) => div !== activeCommentGroupDiv
  )

  const sortedInactiveCommentGroupDivsWithPageData = lodashSortBy(
    inactiveCommentGroupDivsWithPageData,
    [({ pageTop, div }) => pageTop + parseFloat(div.dataset.originalTop)]
  )

  const activeCommentGroupDivWithPageData =
    allCommentGroupDivsWithPageData.find(
      ({ div }) => div === activeCommentGroupDiv
    ) || null

  return {
    activeCommentGroupDivWithPageData,
    inactiveCommentGroupDivsWithPageData: sortedInactiveCommentGroupDivsWithPageData
  }
}

export function positionActiveComment(
  activeCommentGroupDivWithPageData,
  inactiveCommentGroupDivsWithPageData
) {
  const isThereAnActiveComment = activeCommentGroupDivWithPageData != null

  if (isThereAnActiveComment) {
    // align top of active comment group div to annotation
    activeCommentGroupDivWithPageData.div.style.top = `${activeCommentGroupDivWithPageData.div.dataset.originalTop}px`

    const topOfActiveComment = activeCommentGroupDivWithPageData
      ? parseFloat(activeCommentGroupDivWithPageData.div.dataset.originalTop) +
        activeCommentGroupDivWithPageData.pageTop
      : 0

    const isAboveActiveComment = isOriginalTopAbove(topOfActiveComment)

    const previousComments = inactiveCommentGroupDivsWithPageData.filter(
      isAboveActiveComment
    )

    const isBelowActiveComment = isBelowOriginalTop(topOfActiveComment)

    const heightOfActiveComment = parseFloat(
      activeCommentGroupDivWithPageData.div.clientHeight
    )

    return {
      heightOfActiveComment,
      isAboveActiveComment,
      isBelowActiveComment,
      previousComments,
      topOfActiveComment
    }
  } else {
    return {
      heightOfActiveComment: 0,
      isAboveActiveComment: isOriginalTopAbove(0),
      isBelowActiveComment: isBelowOriginalTop(0),
      previousComments: [],
      topOfActiveComment: 0
    }
  }
}

export function positionCommentGroupsBelowActiveCommentGroup(
  heightOfActiveComment,
  inactiveCommentGroupDivsWithPageData,
  isBelowActiveComment,
  topOfActiveComment // relative to document
) {
  const subsequentComments = inactiveCommentGroupDivsWithPageData.filter(
    isBelowActiveComment
  )

  let bottomOfLast = topOfActiveComment + heightOfActiveComment

  for (let { pageTop, div } of subsequentComments) {
    const doesOverlapWithPrevious =
      pageTop + parseFloat(div.dataset.originalTop) <=
      bottomOfLast + MARGIN_BETWEEN_COMMENT_GROUPS

    const newTop = doesOverlapWithPrevious
      ? bottomOfLast + MARGIN_BETWEEN_COMMENT_GROUPS
      : pageTop + parseFloat(div.dataset.originalTop)

    div.style.top = `${newTop - pageTop}px` // relative to page

    bottomOfLast = newTop + div.clientHeight
  }
}

export function positionCommentGroupsAboveActiveCommentGroup__first_pass(
  previousComments
) {
  let bottomOfLast = 0 // relative to document

  for (let { pageTop, div } of previousComments) {
    const doesOverlapWithCommentAbove =
      pageTop + parseFloat(div.dataset.originalTop) <
      bottomOfLast + MARGIN_BETWEEN_COMMENT_GROUPS

    const tentativeTop = doesOverlapWithCommentAbove
      ? bottomOfLast + MARGIN_BETWEEN_COMMENT_GROUPS
      : pageTop + parseFloat(div.dataset.originalTop)

    div.dataset.tentativeTop = tentativeTop // relative to document

    div.style.top = `${tentativeTop - pageTop}px`

    bottomOfLast = tentativeTop + div.clientHeight
  }
}

export function positionCommentGroupsAboveActiveCommentGroup__second_pass(
  previousComments,
  topOfActiveComment // relative to document
) {
  let areSomeCommentsAboveThreshhold = false

  let lowestCommentAboveThreshhold = null

  let topOfCommentBelow = topOfActiveComment

  const previousCommentsDescending = previousComments.slice().reverse()

  for (let { pageTop, div } of previousCommentsDescending) {
    const tentativeTop = parseFloat(div.dataset.tentativeTop) // relative to document

    const divHeight = div.clientHeight

    const doesCommentGroupOverlapWithCommentGroupBelow =
      tentativeTop + divHeight + MARGIN_BETWEEN_COMMENT_GROUPS >=
      topOfCommentBelow

    // relative to document
    const newTop = doesCommentGroupOverlapWithCommentGroupBelow
      ? topOfCommentBelow - divHeight - MARGIN_BETWEEN_COMMENT_GROUPS
      : tentativeTop

    // TODO: why 6? :asci shrug:
    if (newTop < previousComments[0].pageTop + 6) {
      areSomeCommentsAboveThreshhold = true

      lowestCommentAboveThreshhold =
        lowestCommentAboveThreshhold || div.dataset.annotationId
    }

    div.style.top = `${newTop - pageTop}px`

    topOfCommentBelow = newTop
  }
}

export function getCachedClientHeight(node) {
  if (node.dataset.clientHeight == null || Math.random() > 0.9) {
    const clientHeight = node.clientHeight

    node.dataset.clientHeight = clientHeight

    return clientHeight
  }

  return parseFloat(node.dataset.clientHeight)
}

export function isElementFreetextAnnotation(element) {
  if (element instanceof HTMLElement === false) {
    return false
  }
  return element.querySelector('.Freetext-content') != null
}

export const isPenEvent = event => {
  if (!event) {
    return false
  }
  if (event.persist) {
    event.persist()
  }
  if (event.pointerType) {
    return event.pointerType === PEN_POINTER_TYPE
  }
  if (event.target && event.target.pointerType) {
    return event.target.pointerType === PEN_POINTER_TYPE
  }
  return false
}

export const isTouchEvent = event => {
  return (
    event.pointerType === TOUCH_POINTER_TYPE ||
    event.type.includes(TOUCH_POINTER_TYPE)
  )
}
